/**
 * Feature Flags Context
 * 
 * Provides feature flags from Hasura with fallback to local constants.
 * Supports real-time updates via GraphQL subscription (when available).
 */

import { FEATURE_FLAGS, FeatureFlagName } from "constants/featureFlags";
import { useGetAllFeatureFlagsQuery } from "graphql/generated";
import { createContext, ReactNode, useMemo } from "react";

/** Feature flag data from Hasura */
export interface FeatureFlagData {
    id: string;
    name: string;
    enabled: boolean;
    rules: any | null;
    category?: string;
}

/** Feature flags context value */
interface FeatureFlagsContextValue {
    /** Feature flags map (name -> data) */
    flags: Map<string, FeatureFlagData>;
    /** Loading state */
    loading: boolean;
    /** Error state */
    error: Error | null;
    /** Whether flags are from Hasura (true) or constants (false) */
    isFromHasura: boolean;
    /** Refetch flags from Hasura */
    refetch: () => void;
}

export const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
    flags: new Map(),
    loading: false,
    error: null,
    isFromHasura: false,
    refetch: () => {}
});

interface FeatureFlagsProviderProps {
    children: ReactNode;
    /**
     * Whether to fetch from Hasura
     * Set to false to always use local constants (useful for Chrome extension static build)
     */
    enableHasura?: boolean;
}

/**
 * Feature Flags Provider
 * 
 * Fetches feature flags from Hasura and provides them to the app.
 * Falls back to local constants if Hasura is unavailable.
 * 
 * @example
 * ```tsx
 * // In _app.tsx
 * <FeatureFlagsProvider enableHasura={process.env.BUILD_TARGET !== "extension"}>
 *   <Component {...pageProps} />
 * </FeatureFlagsProvider>
 * ```
 */
export function FeatureFlagsProvider({
    children,
    enableHasura = true
}: FeatureFlagsProviderProps) {
    // Convert local constants to FeatureFlagData format
    const getLocalFlags = (): Map<string, FeatureFlagData> => {
        const localFlags = new Map<string, FeatureFlagData>();

        Object.entries(FEATURE_FLAGS).forEach(([name, value]) => {
            const enabled = typeof value === "boolean" ? value : true;
            const rules =
                typeof value === "boolean" || value === null ? null : value;

            localFlags.set(name, {
                id: `local-${name}`,
                name,
                enabled,
                rules,
                category: name.startsWith("CHRISTMAS")
                    ? "christmas"
                    : name.startsWith("VOTING")
                      ? "voting"
                      : "other"
            });
        });

        return localFlags;
    };

    // Fetch from Hasura using generated hook
    const {
        data: hasuraData,
        loading: hasuraLoading,
        error: hasuraError,
        refetch
    } = useGetAllFeatureFlagsQuery({
        skip: !enableHasura,
        fetchPolicy: "network-only" // Always fetch fresh data to avoid stale cache
    });

    // Transform Hasura data to Map format
    const flags = useMemo(() => {
        if (hasuraData?.mix_feature_flag) {
            const hasuraFlags = new Map<string, FeatureFlagData>();
            hasuraData.mix_feature_flag.forEach((flag) => {
                hasuraFlags.set(flag.name, {
                    id: flag.id,
                    name: flag.name,
                    enabled: flag.enabled,
                    rules: flag.rules,
                    category: flag.category || undefined
                });
            });
            return hasuraFlags;
        }
        // Fallback to local constants
        return getLocalFlags();
    }, [hasuraData]);

    const value: FeatureFlagsContextValue = {
        flags,
        loading: hasuraLoading,
        error: hasuraError || null,
        isFromHasura: !!hasuraData?.mix_feature_flag,
        refetch: () => {
            refetch();
        }
    };

    return (
        <FeatureFlagsContext.Provider value={value}>
            {children}
        </FeatureFlagsContext.Provider>
    );
}
